import { formatShortDate } from '@yourbarmate/commons/date-and-time/index.js';
import { html } from 'lit';
import { AccountUsageController } from '../../lib/controllers/account-usage.js';
import { BaseElement } from '../base.js';

const DAY_MS = 86400000;
const modules = [
  { key: 'shop', feature: 'SHOP', name: 'Shop', icon: 'cart' },
  {
    key: 'inventory',
    feature: 'INSPECTIONS',
    name: 'Inventory',
    icon: 'clipboard-check'
  },
  {
    key: 'transfer',
    feature: 'INTERNAL_ORDERS',
    name: 'Transfer',
    icon: 'arrow-left-right'
  },
  {
    key: 'calculations',
    feature: 'CALCULATIONS',
    name: 'Calculations',
    icon: 'calculator'
  }
];

export class AccountUsage extends BaseElement {
  static properties = {
    account: {},
    features: { state: true },
    staff: { state: true },
    created: { state: true }
  };

  constructor() {
    super();
    this.account = '';
    this.features = [];
    this.staff = undefined;
    this.created = undefined;
    this.usage = new AccountUsageController(this);
  }

  render() {
    if (this.staff !== undefined) {
      return renderStaffUsage(this.staff, this.created);
    }

    return this.usage.render((usage) => {
      const { created } = usage.account;
      const enabled_modules = this.features.includes('MIGRATED')
        ? modules.filter(({ feature }) => this.features.includes(feature))
        : [];
      const used_modules = enabled_modules.filter(
        ({ key }) => usage[key]
      ).length;
      const overall_activity = usage.latest;
      const overall_status = activityStatus(overall_activity);
      return html`
        <div class="vstack gap-4">
          <div class="row row-cols-1 row-cols-md-2 g-3">
            ${summaryCard(
              'Overall activity',
              html`<span class="badge text-bg-${overall_status.color}">
                ${overall_status.label}
              </span>`,
              overall_activity
                ? relativeDays(overall_activity)
                : 'No activity recorded'
            )}
            ${summaryCard(
              'Last activity',
              formatDate(overall_activity),
              overall_activity
                ? relativeDays(overall_activity)
                : 'No activity recorded'
            )}
            ${summaryCard(
              'Modules used',
              `${used_modules} / ${enabled_modules.length}`,
              moduleUsageDescription(used_modules, enabled_modules.length)
            )}
            ${summaryCard(
              'Account age',
              `${daysSince(created)} days`,
              `Created ${formatDate(created)}`
            )}
          </div>

          <div>
            <h5 class="mb-3">Module activity</h5>
            ${enabled_modules.length
              ? html`
                  <div class="row row-cols-1 row-cols-md-2 g-3">
                    ${enabled_modules.map((module) =>
                      renderModuleCard(module, usage)
                    )}
                  </div>
                `
              : html`
                  <div class="alert alert-secondary mb-0">
                    No Main App modules enabled.
                  </div>
                `}
          </div>
        </div>
      `;
    });
  }
}

function renderStaffUsage(staff, created) {
  if (!staff) {
    return html`
      <div class="alert alert-warning mb-0">No linked staff profile found.</div>
    `;
  }

  const roles = staff.roles.map(({ name }) => name).join(', ') || 'No role';
  const locations = staff.all_locations
    ? 'All locations'
    : `${staff.locations.length} ${
        staff.locations.length === 1 ? 'location' : 'locations'
      }`;
  const location_names = staff.all_locations
    ? 'Not restricted'
    : staff.locations.map(({ name }) => name).join(', ') || 'No locations';
  const enabled_modules = modules.filter(({ key }) =>
    staff.modules.includes(key)
  );

  return html`
    <div class="vstack gap-4">
      <div class="row row-cols-1 row-cols-md-2 g-3">
        ${summaryCard(
          'Status',
          html`<span
            class="badge text-bg-${staff.enabled ? 'success' : 'danger'}"
          >
            ${staff.enabled ? 'Active' : 'Inactive'}
          </span>`,
          staff.enabled ? 'Login enabled' : 'Login disabled'
        )}
        ${summaryCard(
          'Last login',
          formatDate(staff.last_login),
          staff.last_login ? relativeDays(staff.last_login) : 'Never logged in'
        )}
        ${summaryCard('Role', roles, 'Controls available actions')}
        ${staff.has_locations
          ? summaryCard('Location access', locations, location_names)
          : null}
        ${summaryCard(
          'Account age',
          `${daysSince(created)} days`,
          `Created ${formatDate(created)}`
        )}
      </div>

      <div>
        <h5 class="mb-3">Available Main App modules</h5>
        ${enabled_modules.length
          ? html`
              <div class="row row-cols-1 row-cols-md-2 g-3">
                ${enabled_modules.map(renderStaffModuleCard)}
              </div>
            `
          : html`
              <div class="alert alert-secondary mb-0">
                No Main App modules available for this role.
              </div>
            `}
      </div>
    </div>
  `;
}

function renderStaffModuleCard(module) {
  return html`
    <div class="col">
      <div class="card h-100">
        <div class="card-body d-flex align-items-center gap-2">
          <i class="bi bi-${module.icon}"></i>
          <strong class="me-auto">${module.name}</strong>
          <span class="badge text-bg-primary">Available</span>
        </div>
      </div>
    </div>
  `;
}

function moduleUsageDescription(used, enabled) {
  if (enabled === 0) {
    return 'No modules enabled';
  }
  if (used === enabled) {
    return 'All modules used';
  }
  return `${enabled - used} never used`;
}

function summaryCard(title, value, description) {
  return html`
    <div class="col">
      <div class="card h-100">
        <div class="card-body">
          <div class="small text-muted mb-2">${title}</div>
          <div class="fs-5 fw-semibold">${value}</div>
          <small class="text-muted">${description}</small>
        </div>
      </div>
    </div>
  `;
}

function renderModuleCard(module, usage) {
  const date = usage[module.key];
  const status = activityStatus(date);
  return html`
    <div class="col">
      <div class="card h-100">
        <div class="card-header d-flex align-items-center gap-2">
          <i class="bi bi-${module.icon}"></i>
          <strong class="me-auto">${module.name}</strong>
          <span class="badge text-bg-${status.color}">${status.label}</span>
        </div>
        <div class="card-body">
          <div class="fw-semibold">${formatDate(date)}</div>
          <small class="text-muted">
            ${date ? relativeDays(date) : 'No activity recorded'}
          </small>
        </div>
      </div>
    </div>
  `;
}

function activityStatus(date) {
  if (!date) {
    return { label: 'Never used', color: 'secondary' };
  }
  const days = daysSince(date);
  if (days <= 7) {
    return { label: 'Active', color: 'success' };
  }
  if (days <= 30) {
    return { label: 'Low activity', color: 'warning' };
  }
  return { label: 'Inactive', color: 'danger' };
}

function daysSince(date) {
  return Math.max(
    0,
    Math.floor((Date.now() - new Date(date).getTime()) / DAY_MS)
  );
}

function relativeDays(date) {
  const days = daysSince(date);
  if (days === 0) {
    return 'Today';
  }
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

function formatDate(date) {
  return date ? formatShortDate(date) : 'Never';
}

customElements.define('account-usage', AccountUsage);
